import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Watch, Smartphone, Heart, Activity, TrendingUp, AlertCircle, CheckCircle, Zap, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WearableIntegration = ({ token }) => {
  const { t } = useTranslation();
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [healthData, setHealthData] = useState({
    steps: 0,
    heartRate: 0,
    caloriesBurned: 0,
    activeMinutes: 0,
    sleepHours: 0
  });
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    fitbit: false,
    apple: false,
    google: false
  });

  useEffect(() => {
    fetchConnectedDevices();
    fetchHealthData();
  }, []);

  const fetchConnectedDevices = async () => {
    try {
      const response = await axios.get(`${API}/connect/fitbit/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const isFitbitConnected = response.data.connected || false;
      
      // Update connection status
      setConnectionStatus({
        fitbit: isFitbitConnected,
        apple: false,
        google: false
      });
      
      if (isFitbitConnected) {
        setConnectedDevices([{ type: 'fitbit', ...response.data }]);
      } else {
        setConnectedDevices([]);
      }
    } catch (error) {
      console.error('Error fetching connected devices:', error);
    }
  };

  const fetchHealthData = async () => {
    try {
      const response = await axios.get(`${API}/wearables/health-data?days=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.data;
      if (data) {
        setHealthData({
          steps: data.steps || 0,
          heartRate: data.avg_heart_rate || 0,
          caloriesBurned: data.calories_burned || 0,
          activeMinutes: data.active_minutes || 0,
          sleepHours: data.sleep_hours || 0
        });
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    }
  };

  const connectFitbit = async () => {
    setLoading(true);
    try {
      // Get Fitbit OAuth URL
      const response = await axios.get(`${API}/connect/fitbit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.auth_url) {
        // Redirect to Fitbit OAuth
        window.location.href = response.data.auth_url;
      } else {
        alert('Failed to get Fitbit authorization URL');
      }
    } catch (error) {
      console.error('Error connecting to Fitbit:', error);
      alert(error.response?.data?.detail || 'Failed to connect to Fitbit');
    } finally {
      setLoading(false);
    }
  };

  const syncFitbitData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/connect/fitbit/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Fitbit synced! Steps: ${response.data.synced_data.steps}, Calories: ${response.data.synced_data.calories_burned}`);
      fetchHealthData();
    } catch (error) {
      console.error('Error syncing Fitbit:', error);
      alert(error.response?.data?.detail || 'Failed to sync Fitbit data');
    } finally {
      setLoading(false);
    }
  };

  const disconnectFitbit = async () => {
    if (!window.confirm('Are you sure you want to disconnect Fitbit?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/connect/fitbit/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Fitbit disconnected successfully');
      fetchConnectedDevices();
    } catch (error) {
      console.error('Error disconnecting Fitbit:', error);
      alert(error.response?.data?.detail || 'Failed to disconnect Fitbit');
    } finally {
      setLoading(false);
    }
  };

  const connectAppleHealth = async () => {
    // Apple HealthKit integration would require native iOS app
    alert('Apple HealthKit integration requires the iOS mobile app. Feature coming soon!');
  };

  const connectGoogleFit = async () => {
    // Google Fit integration
    alert('Google Fit integration coming soon! For now, you can manually sync your data.');
  };

  const wearableDevices = [
    {
      name: 'Fitbit',
      type: 'fitbit',
      icon: <Watch size={20} className="text-blue-500" />,
      description: t('wearable.fitbitDesc'),
      connected: connectionStatus.fitbit,
      connectAction: connectFitbit,
      available: true
    },
    {
      name: 'Apple HealthKit',
      type: 'apple_health',
      icon: <Smartphone size={20} className="text-gray-600 dark:text-gray-300" />,
      description: t('wearable.appleHealthDesc'),
      connected: connectionStatus.apple,
      connectAction: connectAppleHealth,
      available: false // Requires iOS app
    },
    {
      name: 'Google Fit',
      type: 'google_fit',
      icon: <Activity size={20} className="text-green-500" />,
      description: t('wearable.googleFitDesc'),
      connected: connectionStatus.google,
      connectAction: connectGoogleFit,
      available: false // Coming soon
    }
  ];

  return (
    <div className="space-y-4" data-testid="wearable-integration">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Watch size={20} className="text-[#FF6600]" />
          Wearable Integrations
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('wearable.subtitle')}
        </p>
      </div>

      {/* Device Connections */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h3 className="font-bold mb-4">{t('wearable.connectDevices')}</h3>
        
        <div className="space-y-4">
          {wearableDevices.map((device) => (
            <div key={device.type} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  {device.icon}
                  <div>
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      {device.name}
                      {device.connected && (
                        <CheckCircle size={14} className="text-green-500" />
                      )}
                      {!device.available && (
                        <AlertCircle size={14} className="text-orange-500" />
                      )}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{device.description}</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {device.connected ? (
                    <>
                      <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                        ✅ {t('wearable.connected')}
                      </span>
                      {device.type === 'fitbit' && (
                        <>
                          <button
                            onClick={syncFitbitData}
                            disabled={loading}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500 text-white hover:bg-blue-600"
                          >
                            {t('wearable.syncNow')}
                          </button>
                          <button
                            onClick={disconnectFitbit}
                            disabled={loading}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600"
                          >
                            {t('wearable.disconnect')}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={device.connectAction}
                      disabled={loading || !device.available}
                      className={`px-4 py-1.5 rounded-lg text-xs font-medium ${
                        device.available
                          ? 'bg-[#FF6600] text-white hover:bg-[#E55C00]'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {device.available ? t('wearable.connect') : t('wearable.comingSoon')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Health Data Display */}
      {connectionStatus.fitbit && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Activity size={18} className="text-blue-500" />
            Today's Activity from Fitbit
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-lg text-center">
              <div className="text-2xl mb-2">👟</div>
              <div className="font-bold text-[#FF6600]">{healthData.steps.toLocaleString()}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('wearable.steps')}</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-lg text-center">
              <div className="text-2xl mb-2">🔥</div>
              <div className="font-bold text-[#FF6600]">{Math.round(healthData.caloriesBurned)}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('wearable.calories')}</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-lg text-center">
              <div className="text-2xl mb-2">⏱️</div>
              <div className="font-bold text-[#FF6600]">{healthData.activeMinutes}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('wearable.activeMinutes')}</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-lg text-center">
              <div className="text-2xl mb-2">❤️</div>
              <div className="font-bold text-[#FF6600]">{healthData.heartRate || 'N/A'}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('wearable.heartRate')}</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-4 rounded-lg text-center">
              <div className="text-2xl mb-2">😴</div>
              <div className="font-bold text-[#FF6600]">{healthData.sleepHours.toFixed(1)}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('wearable.sleepHours')}</div>
            </div>
          </div>
          
          <button
            onClick={syncFitbitData}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium flex items-center gap-2 mx-auto"
          >
            <Zap size={14} />
            {loading ? t('wearable.syncing') : t('wearable.refreshData')}
          </button>
        </div>
      )}

      {/* Setup Instructions */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h3 className="font-bold mb-4">{t('wearable.setupInstructions')}</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-semibold text-sm mb-1">{t('wearable.fitbitSetup')}</h4>
            <ol className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>1. {t('wearable.fitbitStep1')}</li>
              <li>2. {t('wearable.fitbitStep2')}</li>
              <li>3. {t('wearable.fitbitStep3')}</li>
              <li>4. {t('wearable.fitbitStep4')}</li>
            </ol>
          </div>
          
          <div className="border-l-4 border-gray-500 pl-4">
            <h4 className="font-semibold text-sm mb-1">{t('wearable.appleSetup')}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {t('wearable.appleDesc')}
            </p>
          </div>
          
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-semibold text-sm mb-1">{t('wearable.googleFitSetup')}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {t('wearable.googleFitComingSoonDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WearableIntegration;
